import {
  Add,
  Delete,
  Edit,
  Settings,
  PersonAdd as ConvertIcon,
  TrendingUp,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ViewKanban,
  TableChart,
  AssignmentInd,
  Sort as SortIcon,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Slider,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Autocomplete
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { navigate } from '@reach/router';
import { useEffect, useMemo, useState } from 'react';
import apiClient from '../api/client';
import AdvancedFilters from '../components/AdvancedFilters';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import AsyncTableSearch from '../components/AsyncTableSearch';
import Layout from '../components/Layout';
import LeadKanban from '../components/LeadKanban';
import SchemaEditor from '../components/SchemaEditor';
import StoragePickerDialog from '../components/StoragePickerDialog';
import StyledTableContainer from '../components/StyledTableContainer';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';
import { useLeads, useUI } from '../store';

// Lead status configurations
const LEAD_STATUSES = {
  new: { label: 'Новый', color: '#2196F3', icon: '🆕' },
  contacted: { label: 'В контакте', color: '#FF9800', icon: '📞' },
  qualified: { label: 'Квалифицирован', color: '#4CAF50', icon: '✅' },
  converted: { label: 'Конвертирован', color: '#9C27B0', icon: '🎯' },
  rejected: { label: 'Отклонен', color: '#F44336', icon: '❌' }
};

// Lead source configurations
const LEAD_SOURCES = {
  website: { label: 'Сайт', icon: '🌐' },
  ads: { label: 'Реклама', icon: '📢' },
  referral: { label: 'Рекомендация', icon: '👥' },
  cold_call: { label: 'Холодный звонок', icon: '📞' },
  event: { label: 'Мероприятие', icon: '🎪' },
  social: { label: 'Соц.сети', icon: '📱' },
  other: { label: 'Другое', icon: '❓' }
};

const LeadStatusChip = ({ status }) => {
  const statusInfo = LEAD_STATUSES[status] || { label: status, color: 'default', icon: '?' };
  return (
    <Chip
      label={statusInfo.label}
      icon={<span>{statusInfo.icon}</span>}
      size="small"
      sx={{
        backgroundColor: statusInfo.color,
        color: 'white',
        fontWeight: 500
      }}
    />
  );
};

const LeadSourceChip = ({ source }) => {
  const sourceInfo = LEAD_SOURCES[source] || { label: source, icon: '?' };
  return (
    <Chip
      label={sourceInfo.label}
      icon={<span>{sourceInfo.icon}</span>}
      size="small"
      variant="outlined"
      sx={{ borderColor: 'rgba(255,255,255,0.3)' }}
    />
  );
};

const getScoreColor = (score) => {
  if (score >= 80) return '#4CAF50';
  if (score >= 60) return '#FF9800';
  if (score >= 40) return '#FFC107';
  return '#F44336';
};

const LeadsPage = () => {
  const { user, loading: authLoading } = useAuth();
  useRealtimeSSE();

  // Zustand store
  const {
    leads: storeLeads,
    loading: storeLoading,
    error: storeError,
    fetchLeads,
    createLead,
    updateLead,
    deleteLead
  } = useLeads();

  const { addNotification } = useUI();

  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [convertDialog, setConvertDialog] = useState(false);
  const [activityDialog, setActivityDialog] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityItems, setActivityItems] = useState([]);
  const [view, setView] = useState('table');

  // Пагинация
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [totalPages, setTotalPages] = useState(null);

  // Поиск
  const [searchActive, setSearchActive] = useState(false);
  const [searchPage, setSearchPage] = useState(1);

  // Сортировка
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchPagination, setSearchPagination] = useState({ page: 1, pages: 1, total: 0, perPage: 20 });

  // Кастомные поля
  const [leadSchema, setLeadSchema] = useState([]);
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [presignedMap, setPresignedMap] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerImagesOnly, setPickerImagesOnly] = useState(false);
  const [pickerTargetField, setPickerTargetField] = useState(null);

  // Формы
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    source: 'website',
    status: 'new',
    score: 0,
    notes: '',
    tags: [],
    assigned_to: '',
    customFields: {}
  });

  const [convertForm, setConvertForm] = useState({
    create_deal: true,
    deal_title: '',
    deal_amount: 0,
    pipeline_id: ''
  });

  // Поля для фильтров
  const baseFilterFields = [
    { value: 'name', label: 'Имя' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Телефон' },
    { value: 'company', label: 'Компания' },
    { value: 'source', label: 'Источник' },
    { value: 'status', label: 'Статус' },
    { value: 'score', label: 'Рейтинг' },
  ];

  const allFilterFields = useMemo(() => [
    ...baseFilterFields,
    ...leadSchema.map(f => ({ value: f.name, label: f.name }))
  ], [leadSchema]);

  // Статистика
  const statistics = useMemo(() => {
    const data = searchActive ? filteredLeads : leads;

    const total = data.length;
    const newLeads = data.filter(l => l.status === 'new');
    const contacted = data.filter(l => l.status === 'contacted');
    const qualified = data.filter(l => l.status === 'qualified');
    const converted = data.filter(l => l.status === 'converted');
    const conversionRate = total > 0 ? ((converted.length / total) * 100).toFixed(1) : 0;
    const avgScore = total > 0 ? (data.reduce((sum, l) => sum + (l.score || 0), 0) / total).toFixed(1) : 0;

    return {
      total,
      new: newLeads.length,
      contacted: contacted.length,
      qualified: qualified.length,
      converted: converted.length,
      conversionRate,
      avgScore
    };
  }, [leads, filteredLeads, searchActive]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (user) {
      fetchLeadsData();
      fetchUsers();
      fetchPipelines();
      fetchSchema();
    }
  }, [user, authLoading]);

  // Локальная фильтрация (только при неактивном серверном поиске)
  useEffect(() => {
    if (searchActive) return;

    if (leads.length > 0) {
      let filtered = leads;

      // Применяем расширенные фильтры
      if (advancedFilters.length > 0) {
        filtered = filtered.filter(lead => {
          return advancedFilters.every(filter => {
            const value = filter.field.includes('customFields.')
              ? lead.customFields?.[filter.field.replace('customFields.', '')]
              : lead[filter.field];

            if (value === null || value === undefined) return false;

            const stringValue = String(value).toLowerCase();
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
              case 'contains':
                return stringValue.includes(filterValue);
              case 'equals':
                return stringValue === filterValue;
              case 'startsWith':
                return stringValue.startsWith(filterValue);
              case 'endsWith':
                return stringValue.endsWith(filterValue);
              case 'greaterThan':
                return Number(value) > Number(filter.value);
              case 'lessThan':
                return Number(value) < Number(filter.value);
              default:
                return true;
            }
          });
        });
      }

      // Применяем сортировку
      filtered = filtered.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        // Обработка вложенных полей (customFields)
        if (sortBy.includes('customFields.')) {
          const field = sortBy.replace('customFields.', '');
          aValue = a.customFields?.[field];
          bValue = b.customFields?.[field];
        }

        // Обработка пустых значений
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Сравнение
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue?.toLowerCase() || '';
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });

      setFilteredLeads(filtered);
    } else {
      setFilteredLeads([]);
    }
  }, [leads, advancedFilters, searchActive, sortBy, sortOrder]);

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/users');
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Ошибка при загрузке пользователей:', err);
    }
  };

  const fetchPipelines = async () => {
    try {
      const response = await apiClient.get('/pipelines');
      setPipelines(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Ошибка при загрузке воронок:', err);
    }
  };

  const fetchLeadsData = async (pageParam = page) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/leads?page=${pageParam}&per_page=${perPage}`);

      const leadsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setLeads(leadsData);

      const pagination = response.pagination || response.data || {};
      const pages = pagination.pages || pagination.total_pages || 1;
      setTotalPages(pages);

      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке лидов:', err);
      setError('Не удалось загрузить список лидов');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchema = async () => {
    try {
      const resp = await apiClient.get('/leads/schema');
      setLeadSchema(resp.data.schema || []);
    } catch (err) {
      console.error('Не удалось получить схему лидов', err);
    }
  };

  const getUserName = (userId) => {
    if (!userId) return '—';
    const userObj = users.find(u => u.id === userId || u._id === userId);
    return userObj ? userObj.name || userObj.email : 'Неизвестный пользователь';
  };

  const handleOpenDialog = (lead = null) => {
    if (lead) {
      setEditingLead(lead);
      const customFieldsValues = {};
      leadSchema.forEach(f => {
        customFieldsValues[f.name] = lead.customFields?.[f.name] ?? '';
      });
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        position: lead.position || '',
        source: lead.source || 'website',
        status: lead.status || 'new',
        score: lead.score || 0,
        notes: lead.notes || '',
        tags: lead.tags || [],
        assigned_to: lead.assigned_to || '',
        customFields: customFieldsValues
      });
    } else {
      setEditingLead(null);
      const customDefaults = {};
      leadSchema.forEach(f => {
        customDefaults[f.name] = f.default ?? '';
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        position: '',
        source: 'website',
        status: 'new',
        score: 0,
        notes: '',
        tags: [],
        assigned_to: '',
        customFields: customDefaults
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData({
      ...formData,
      customFields: {
        ...formData.customFields,
        [fieldName]: value
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      ...formData,
      customFields: formData.customFields
    };

    try {
      if (editingLead) {
        await apiClient.put(`/leads/${editingLead.id || editingLead._id}`, data);
        addNotification('success', 'Лид обновлен успешно');
      } else {
        await apiClient.post('/leads', data);
        addNotification('success', 'Лид создан успешно');
      }
      handleCloseDialog();
      fetchLeadsData();
    } catch (err) {
      console.error('Ошибка при сохранении лида:', err);
      setError('Не удалось сохранить данные лида');
      addNotification('error', err.response?.data?.message || 'Ошибка при сохранении лида');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этого лида?')) {
      try {
        await apiClient.delete(`/leads/${id}`);
        addNotification('success', 'Лид удален');
        fetchLeadsData();
      } catch (err) {
        console.error('Ошибка при удалении лида:', err);
        setError('Не удалось удалить лида');
        addNotification('error', 'Не удалось удалить лида');
      }
    }
  };

  const handleConvert = async () => {
    if (!selectedLead) return;

    try {
      const response = await apiClient.post(`/leads/${selectedLead.id || selectedLead._id}/convert`, convertForm);

      if (response.status === 200) {
        addNotification('success', 'Лид успешно конвертирован в клиента');
        setConvertDialog(false);
        setConvertForm({ create_deal: true, deal_title: '', deal_amount: 0, pipeline_id: '' });
        fetchLeadsData();
      }
    } catch (err) {
      console.error('Ошибка конвертации лида:', err);
      addNotification('error', err.response?.data?.message || 'Ошибка конвертации лида');
    }
  };

  const openConvertDialog = (lead) => {
    setSelectedLead(lead);
    setConvertForm({
      create_deal: true,
      deal_title: `Сделка с ${lead.company || lead.name}`,
      deal_amount: 0,
      pipeline_id: pipelines.length > 0 ? (pipelines[0].id || pipelines[0]._id) : ''
    });
    setConvertDialog(true);
  };

  const openActivityDialog = async (lead) => {
    setSelectedLead(lead);
    setActivityDialog(true);
    setActivityLoading(true);
    try {
      const res = await apiClient.get(`/leads/${lead.id || lead._id}/activity`, { params: { page: 1, per_page: 50 } });
      setActivityItems(res.data?.data || []);
    } catch (e) {
      addNotification('error', 'Не удалось загрузить активность лида');
      setActivityItems([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => setAdvancedFilters([]);

  const handlePageChange = (e, value) => {
    if (searchActive) {
      setSearchPage(value);
    } else {
      setPage(value);
      fetchLeadsData(value);
    }
  };

  // Callbacks для поиска
  const handleSearchResults = (items) => {
    setFilteredLeads(items);
  };

  const handleSearchPaginationChange = (paginationInfo) => {
    setSearchPagination(paginationInfo);
  };

  const handleSearchActiveChange = (active) => {
    setSearchActive(active);
    if (!active) {
      setFilteredLeads(leads);
      setSearchPage(1);
      setSearchPagination({ page: 1, pages: 1, total: 0, perPage: 20 });
    }
  };

  // Схема
  const handleSaveSchema = async (newSchema) => {
    try {
      await apiClient.put('/leads/schema', { schema: newSchema });
      setLeadSchema(newSchema);
      setSchemaDialogOpen(false);
      fetchLeadsData();
      addNotification('success', 'Схема сохранена');
    } catch (err) {
      console.error('Ошибка при сохранении схемы:', err);
      addNotification('error', 'Не удалось сохранить схему');
    }
  };

  // Storage
  const uploadForField = async (fieldName, fileObj) => {
    try {
      const form = new FormData();
      form.append('file', fileObj);
      const uploadResp = await apiClient.post('/storage/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const key = uploadResp.data.key || uploadResp.data.file_key;
      handleCustomFieldChange(fieldName, key);
      addNotification('success', 'Файл загружен');
    } catch (err) {
      console.error(err);
      addNotification('error', 'Ошибка загрузки');
    }
  };

  const fetchPresigned = async (key) => {
    if (!key || presignedMap[key]) return presignedMap[key];
    try {
      const resp = await apiClient.get(`/storage/files/${encodeURIComponent(key)}/url`);
      const url = resp.data.url;
      setPresignedMap(prev => ({ ...prev, [key]: url }));
      return url;
    } catch (err) {
      console.error(err);
    }
    return null;
  };

  const renderFileField = (field, isImage) => {
    const key = formData.customFields[field.name];
    const fileName = key ? String(key).split('/').pop() : '';
    return (
      <Box key={field.name} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{field.name}</Typography>
        {key && (
          <Box sx={{ mb: 1 }}>
            {isImage ? (
              <img
                alt={field.name}
                src={presignedMap[key] || ''}
                style={{ maxWidth: 200, maxHeight: 150, objectFit: 'cover' }}
                onLoad={async () => { if (!presignedMap[key]) await fetchPresigned(key); }}
              />
            ) : (
              <Link
                component="button"
                variant="body2"
                onClick={async () => {
                  const url = await fetchPresigned(key);
                  if (url) window.open(url, '_blank');
                }}
              >
                {fileName}
              </Link>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setPickerTargetField(field.name);
              setPickerImagesOnly(isImage);
              setPickerOpen(true);
            }}
          >
            Выбрать из хранилища
          </Button>
          <Button
            size="small"
            variant="outlined"
            component="label"
          >
            Загрузить
            <input
              type="file"
              hidden
              accept={isImage ? 'image/*' : '*'}
              onChange={(e) => { if (e.target.files[0]) uploadForField(field.name, e.target.files[0]); }}
            />
          </Button>
        </Box>
      </Box>
    );
  };

  const displayData = searchActive ? filteredLeads : filteredLeads;

  return (
    <Layout>
      <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 8px 32px 0 rgba(31,38,135,0.37)' }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: { sm: 'space-between' }, gap: { xs: 2, sm: 0 } }}>
          <Typography variant="h4" component="h1" sx={{ color: 'white' }}>
            Лиды
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' }, gap: { xs: 1, sm: 2 } }}>
            <Button
              variant={view === 'table' ? 'contained' : 'outlined'}
              startIcon={<TableChart />}
              onClick={() => setView('table')}
              sx={{ width: { xs: '100%', sm: 'auto' }, color: view === 'table' ? 'white' : 'white', borderColor: 'white' }}
            >
              Таблица
            </Button>
            <Button
              variant={view === 'kanban' ? 'contained' : 'outlined'}
              startIcon={<ViewKanban />}
              onClick={() => setView('kanban')}
              sx={{ width: { xs: '100%', sm: 'auto' }, color: view === 'kanban' ? 'white' : 'white', borderColor: 'white' }}
            >
              Канбан
            </Button>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setSchemaDialogOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' }, color: 'white', borderColor: 'white' }}
            >
              Настроить поля
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Добавить лида
            </Button>
          </Box>
        </Box>

        {/* Статистика */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'rgba(33, 150, 243, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  Всего лидов
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.total}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Средний рейтинг: {statistics.avgScore}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'rgba(33, 150, 243, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  🆕 Новые
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.new}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'rgba(255, 152, 0, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  📞 В контакте
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.contacted}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  ✅ Квалифицировано
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.qualified}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'rgba(156, 39, 176, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp /> Конверсия
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.conversionRate}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {statistics.converted} конвертировано
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Поиск и фильтры */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <AsyncTableSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onResults={handleSearchResults}
            onActiveChange={handleSearchActiveChange}
            onPaginationChange={handleSearchPaginationChange}
            placeholder="Поиск лидов..."
            searchDoc="leads"
            searchFields={["name", "email", "phone", "company", "notes"]}
            page={searchPage}
            perPage={perPage}
            sx={{ flex: 1 }}
          />
          <AdvancedFilters
            filters={advancedFilters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            fields={allFilterFields}
          />

          {/* Сортировка */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Сортировать по</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Сортировать по"
              size="small"
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '.MuiSvgIcon-root': { color: 'white' }
              }}
            >
              <MenuItem value="created_at">Дате создания</MenuItem>
              <MenuItem value="name">Имени</MenuItem>
              <MenuItem value="company">Компании</MenuItem>
              <MenuItem value="score">Рейтингу</MenuItem>
              <MenuItem value="status">Статусу</MenuItem>
              <MenuItem value="source">Источнику</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
        </Box>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            {error}
          </Paper>
        )}

        {/* Вид: Таблица или Канбан */}
        {view === 'table' ? (
          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'white' }}>Имя</TableCell>
                  <TableCell sx={{ color: 'white' }}>Контакты</TableCell>
                  <TableCell sx={{ color: 'white' }}>Компания</TableCell>
                  <TableCell sx={{ color: 'white' }}>Источник</TableCell>
                  <TableCell sx={{ color: 'white' }}>Статус</TableCell>
                  <TableCell sx={{ color: 'white' }}>Рейтинг</TableCell>
                  <TableCell sx={{ color: 'white' }}>Назначен</TableCell>
                  {leadSchema.map(field => (
                    <TableCell key={field.name} sx={{ color: 'white' }}>{field.name}</TableCell>
                  ))}
                  <TableCell sx={{ color: 'white', position: 'sticky', right: 0, background: 'rgba(30,30,30,0.8)', zIndex: 2 }}>
                    Действия
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8 + leadSchema.length} align="center" sx={{ color: 'white' }}>
                      Загрузка...
                    </TableCell>
                  </TableRow>
                ) : displayData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8 + leadSchema.length} align="center" sx={{ color: 'white' }}>
                      {searchQuery || advancedFilters.length > 0 ? 'Лиды не найдены' : 'Нет лидов'}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayData.map(lead => (
                    <TableRow key={lead.id || lead._id}>
                      <TableCell sx={{ color: 'white' }}>
                        <Typography variant="subtitle2" sx={{ color: 'white' }}>{lead.name}</Typography>
                        {lead.position && (
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{lead.position}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {lead.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <EmailIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
                            <Typography variant="body2" sx={{ color: 'white' }}>{lead.email}</Typography>
                          </Box>
                        )}
                        {lead.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
                            <Typography variant="body2" sx={{ color: 'white' }}>{lead.phone}</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>{lead.company || '—'}</TableCell>
                      <TableCell><LeadSourceChip source={lead.source} /></TableCell>
                      <TableCell><LeadStatusChip status={lead.status} /></TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={lead.score || 0}
                            sx={{
                              width: 60,
                              height: 6,
                              borderRadius: 3,
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getScoreColor(lead.score || 0)
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ color: getScoreColor(lead.score || 0), fontWeight: 600 }}>
                            {lead.score || 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>{getUserName(lead.assigned_to)}</TableCell>
                      {leadSchema.map(field => (
                        <TableCell key={field.name} sx={{ color: 'white' }}>
                          {field.type === 'boolean' ? (
                            lead.customFields?.[field.name] ? 'Да' : 'Нет'
                          ) : field.type === 'image' ? (
                            lead.customFields?.[field.name] ? (
                              <img
                                alt={field.name}
                                src={presignedMap[lead.customFields[field.name]] || ''}
                                style={{ maxWidth: 80, maxHeight: 60, objectFit: 'cover' }}
                                onMouseEnter={async () => { if (!presignedMap[lead.customFields[field.name]]) await fetchPresigned(lead.customFields[field.name]); }}
                              />
                            ) : '—'
                          ) : field.type === 'file' ? (
                            lead.customFields?.[field.name] ? (
                              <Link
                                component="button"
                                variant="body2"
                                onClick={async () => {
                                  const url = await fetchPresigned(lead.customFields[field.name]);
                                  if (url) window.open(url, '_blank');
                                }}
                              >
                                {String(lead.customFields[field.name]).split('/').pop()}
                              </Link>
                            ) : '—'
                          ) : (
                            lead.customFields?.[field.name] ?? '—'
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ position: 'sticky', right: 0, background: 'rgba(30,30,30,0.7)', zIndex: 1 }}>
                        <Tooltip title="Редактировать">
                          <IconButton onClick={() => handleOpenDialog(lead)}>
                            <Edit sx={{ color: 'white' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Активность">
                          <IconButton onClick={() => openActivityDialog(lead)}>
                            <AssignmentInd sx={{ color: 'white' }} />
                          </IconButton>
                        </Tooltip>
                        {lead.status !== 'converted' && (
                          <Tooltip title="Конвертировать">
                            <IconButton onClick={() => openConvertDialog(lead)}>
                              <ConvertIcon sx={{ color: 'white' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Удалить">
                          <IconButton onClick={() => handleDelete(lead.id || lead._id)}>
                            <Delete sx={{ color: 'white' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        ) : (
          <LeadKanban
            leads={displayData}
            onLeadAction={{
              onEdit: handleOpenDialog,
              onDelete: (lead) => handleDelete(lead.id || lead._id),
              onConvert: openConvertDialog,
              onStatusChange: async (leadId, newStatus) => {
                try {
                  await apiClient.put(`/leads/${leadId}`, { status: newStatus });
                  addNotification('success', 'Статус изменен');
                  fetchLeadsData();
                } catch (err) {
                  addNotification('error', 'Ошибка изменения статуса');
                }
              }
            }}
          />
        )}

        {/* Пагинация */}
        {(() => {
          const currentPage = searchActive ? searchPagination.page : page;
          const currentTotalPages = searchActive ? searchPagination.pages : totalPages;
          const currentTotal = searchActive ? searchPagination.total : null;

          if (currentTotalPages <= 1 && !searchActive) return null;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
              {searchActive && currentTotal !== null && (
                <Typography variant="body2" color="text.secondary" sx={{ color: 'white' }}>
                  Найдено: {currentTotal}
                </Typography>
              )}
              {currentTotalPages > 1 && (
                <Pagination
                  color="primary"
                  count={currentTotalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              )}
            </Box>
          );
        })()}
      </Box>

      {/* Диалог создания/редактирования */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingLead ? 'Редактировать лида' : 'Добавить лида'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Имя *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Телефон"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Компания"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Должность"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Источник</InputLabel>
                  <Select name="source" value={formData.source} onChange={handleChange}>
                    {Object.entries(LEAD_SOURCES).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        {config.icon} {config.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Статус</InputLabel>
                  <Select name="status" value={formData.status} onChange={handleChange}>
                    {Object.entries(LEAD_STATUSES).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        {config.icon} {config.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Назначен</InputLabel>
                  <Select name="assigned_to" value={formData.assigned_to} onChange={handleChange}>
                    <MenuItem value="">Не назначен</MenuItem>
                    {users.map(u => (
                      <MenuItem key={u.id || u._id} value={u.id || u._id}>
                        {u.name || u.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom sx={{ mt: 2 }}>Рейтинг: {formData.score}</Typography>
                <Slider
                  value={formData.score}
                  onChange={(e, newValue) => setFormData({ ...formData, score: newValue })}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                  sx={{
                    color: getScoreColor(formData.score)
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  value={formData.tags}
                  onChange={(e, newValue) => setFormData({ ...formData, tags: newValue })}
                  options={[]}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Теги"
                      placeholder="Добавить тег"
                      margin="normal"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Заметки"
                  name="notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>

              {/* Кастомные поля */}
              {leadSchema.map(field => {
                if (field.type === 'text') {
                  return (
                    <Grid item xs={12} md={6} key={field.name}>
                      <TextField
                        fullWidth
                        label={field.name}
                        value={formData.customFields[field.name] || ''}
                        onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                        margin="normal"
                      />
                    </Grid>
                  );
                } else if (field.type === 'number') {
                  return (
                    <Grid item xs={12} md={6} key={field.name}>
                      <TextField
                        fullWidth
                        label={field.name}
                        type="number"
                        value={formData.customFields[field.name] || ''}
                        onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                        margin="normal"
                      />
                    </Grid>
                  );
                } else if (field.type === 'date') {
                  return (
                    <Grid item xs={12} md={6} key={field.name}>
                      <TextField
                        fullWidth
                        label={field.name}
                        type="date"
                        value={formData.customFields[field.name] || ''}
                        onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  );
                } else if (field.type === 'boolean') {
                  return (
                    <Grid item xs={12} md={6} key={field.name}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{field.name}</InputLabel>
                        <Select
                          value={formData.customFields[field.name] !== undefined ? String(formData.customFields[field.name]) : 'false'}
                          onChange={(e) => handleCustomFieldChange(field.name, e.target.value === 'true')}
                        >
                          <MenuItem value="false">Нет</MenuItem>
                          <MenuItem value="true">Да</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                } else if (field.type === 'select') {
                  return (
                    <Grid item xs={12} md={6} key={field.name}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{field.name}</InputLabel>
                        <Select
                          value={formData.customFields[field.name] || ''}
                          onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                        >
                          {(field.options || []).map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                } else if (field.type === 'file') {
                  return (
                    <Grid item xs={12} key={field.name}>
                      {renderFileField(field, false)}
                    </Grid>
                  );
                } else if (field.type === 'image') {
                  return (
                    <Grid item xs={12} key={field.name}>
                      {renderFileField(field, true)}
                    </Grid>
                  );
                }
                return null;
              })}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Диалог конвертации */}
      <Dialog open={convertDialog} onClose={() => setConvertDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Конвертировать лида в клиента</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Лид <strong>{selectedLead?.name}</strong> будет конвертирован в клиента.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={convertForm.create_deal}
                onChange={(e) => setConvertForm(prev => ({
                  ...prev,
                  create_deal: e.target.checked
                }))}
              />
            }
            label="Создать сделку"
          />

          {convertForm.create_deal && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Название сделки"
                value={convertForm.deal_title}
                onChange={(e) => setConvertForm(prev => ({
                  ...prev,
                  deal_title: e.target.value
                }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Сумма сделки"
                type="number"
                value={convertForm.deal_amount}
                onChange={(e) => setConvertForm(prev => ({
                  ...prev,
                  deal_amount: parseFloat(e.target.value) || 0
                }))}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth>
                <InputLabel>Воронка</InputLabel>
                <Select
                  value={convertForm.pipeline_id}
                  onChange={(e) => setConvertForm(prev => ({
                    ...prev,
                    pipeline_id: e.target.value
                  }))}
                >
                  {pipelines.map(p => (
                    <MenuItem key={p.id || p._id} value={p.id || p._id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialog(false)}>Отмена</Button>
          <Button onClick={handleConvert} variant="contained" color="primary">
            Конвертировать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог активности */}
      <Dialog open={activityDialog} onClose={() => setActivityDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Активность лида: {selectedLead?.name}</DialogTitle>
        <DialogContent>
          {activityLoading && <LinearProgress sx={{ mb: 2 }} />}
          {!activityLoading && activityItems.length === 0 && (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Событий пока нет
            </Typography>
          )}
          {!activityLoading && activityItems.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {activityItems.map((item) => (
                <Paper key={item._id || item.id || item.created_at} sx={{ p: 2, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {new Date(item.created_at).toLocaleString()}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: 'white' }}>
                    {item.action}
                  </Typography>
                  {item.metadata && Object.keys(item.metadata).length > 0 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                      {JSON.stringify(item.metadata)}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityDialog(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог схемы */}
      <Dialog open={schemaDialogOpen} onClose={() => setSchemaDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Настроить поля лидов</DialogTitle>
        <DialogContent>
          <SchemaEditor
            schema={leadSchema}
            onChange={(newSchema) => setLeadSchema(newSchema)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchemaDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => handleSaveSchema(leadSchema)}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Storage Picker */}
      <StoragePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(key) => {
          if (pickerTargetField) {
            handleCustomFieldChange(pickerTargetField, key);
          }
          setPickerOpen(false);
        }}
        imagesOnly={pickerImagesOnly}
      />
    </Layout>
  );
};

export default LeadsPage;
